// Multi-cell client routing.
//
// Each user's account + data live in ONE regional "cell". The backend bakes the
// owning cell into the JWT `cell` claim and returns it (plus `api_base_url`) on
// login/refresh. This module turns that into the `cellApiBaseUrl` localStorage
// value that `config.js` reads at runtime, so every API call from a signed-in
// user is routed to their own cell.
//
// It also installs an axios interceptor that transparently follows a backend
// `wrong_cell` 409 (e.g. a stale token, a new auth method, or a request that
// landed on the wrong cell) by adopting the indicated cell and retrying once.

import axios from 'axios';
import { BASE_API_URL } from './config';

const CELL_BASE_URL_KEY = 'cellApiBaseUrl';
const CELL_ID_KEY = 'cellId';
const CELLS_MAP_KEY = 'cellsPublicMap';

export function getCellBaseUrl() {
  try {
    return localStorage.getItem(CELL_BASE_URL_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function setCellRouting(cellId, apiBaseUrl) {
  try {
    if (apiBaseUrl) localStorage.setItem(CELL_BASE_URL_KEY, apiBaseUrl);
    if (cellId) localStorage.setItem(CELL_ID_KEY, cellId);
  } catch (e) {
    /* ignore */
  }
}

export function clearCellRouting() {
  try {
    localStorage.removeItem(CELL_BASE_URL_KEY);
    localStorage.removeItem(CELL_ID_KEY);
    localStorage.removeItem(CELLS_MAP_KEY);
  } catch (e) {
    /* ignore */
  }
}

// Decode the `cell` claim from a JWT without verifying it (routing hint only).
export function decodeJwtCell(token) {
  try {
    const t = (token || '').replace('Bearer ', '');
    const payload = JSON.parse(atob(t.split('.')[1]));
    return payload.cell || null;
  } catch (e) {
    return null;
  }
}

function cachedCellsMap() {
  try {
    return JSON.parse(localStorage.getItem(CELLS_MAP_KEY) || '{}');
  } catch (e) {
    return {};
  }
}

// Fetch + cache the public cell -> api_base_url map from the baked endpoint.
async function fetchCellsMap() {
  const resp = await axios.get(`${BASE_API_URL}/cells/public`);
  const cells = (resp.data && resp.data.cells) || [];
  const map = {};
  cells.forEach((c) => {
    if (c && c.id) map[c.id] = c.api_base_url;
  });
  try {
    localStorage.setItem(CELLS_MAP_KEY, JSON.stringify(map));
  } catch (e) {
    /* ignore */
  }
  return map;
}

// Resolve a cell id to its public API base URL (cached, then fetched).
export async function resolveCellBaseUrl(cellId) {
  if (!cellId) return '';
  let map = cachedCellsMap();
  if (!map[cellId]) {
    try {
      map = await fetchCellsMap();
    } catch (e) {
      /* network/endpoint unavailable — fall through to '' */
    }
  }
  return map[cellId] || '';
}

// Apply routing from an auth response (verify-magic-link / refresh-token).
// Prefers the explicit `api_base_url`; otherwise resolves the JWT `cell` claim
// via the public cell map. No-op (keeps the baked URL) for single-cell installs.
export async function applyCellRouting({ token, cell, apiBaseUrl } = {}) {
  let baseUrl = apiBaseUrl || '';
  const cellId = cell || decodeJwtCell(token);
  if (!baseUrl && cellId) {
    baseUrl = await resolveCellBaseUrl(cellId);
  }
  if (baseUrl) setCellRouting(cellId, baseUrl);
  return baseUrl;
}

// Best-effort boot hydration: if we have a session token but no resolved cell
// URL yet (e.g. first load after this feature shipped), derive it from the
// token's `cell` claim. Fire-and-forget; the 409 interceptor is the backstop.
export async function hydrateFromStoredToken() {
  try {
    if (getCellBaseUrl()) return; // already routed
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const cellId = decodeJwtCell(token);
    if (!cellId) return;
    const baseUrl = await resolveCellBaseUrl(cellId);
    if (baseUrl) setCellRouting(cellId, baseUrl);
  } catch (e) {
    /* ignore — interceptor will recover on the first wrong_cell 409 */
  }
}

function rewriteToBase(url, oldBase, newBase) {
  if (oldBase && url.startsWith(oldBase)) {
    return newBase + url.slice(oldBase.length);
  }
  // Fallback: swap the origin (+ optional /vN prefix) for the new base.
  const m = url.match(/^https?:\/\/[^/]+(\/v\d+)?/);
  if (m) return newBase + url.slice(m[0].length);
  return url;
}

// Install once at app startup. Follows a `wrong_cell` 409 by adopting the
// indicated cell's base URL and retrying the original request there exactly
// once. This is the backstop for stale tokens / future auth methods; the normal
// path is handled proactively by applyCellRouting at login.
export function installCellRoutingInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const resp = error && error.response;
      const cfg = error && error.config;
      // Canonical envelope: {error: {code, message, details}}; older
      // payloads carried the code as a flat string. Accept both.
      const errCode =
        resp && resp.data &&
        (typeof resp.data.error === 'object'
          ? resp.data.error?.code
          : resp.data.error);
      if (
        resp &&
        resp.status === 409 &&
        errCode === 'wrong_cell' &&
        cfg &&
        !cfg.__cellRetried
      ) {
        const newBase = resp.data.redirect_api_base_url;
        if (newBase) {
          const oldBase = getCellBaseUrl() || BASE_API_URL;
          setCellRouting(resp.data.home_cell, newBase);
          cfg.__cellRetried = true;
          cfg.url = rewriteToBase(cfg.url, oldBase, newBase);
          return axios(cfg);
        }
      }
      return Promise.reject(error);
    },
  );
}
